/**
 * Hosting prices (public, plan D10) — what it costs to host an application on
 * Twasta, per person or per server. Every figure comes from the Marketplace
 * policy through GET /build/hosting; nothing here is a literal.
 * Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { money, mktError } from '@/api/marketplace'
import { getHosting, type HostingPrices, type HostingSize } from '@/api/marketplaceBuild'
import MarketplaceShell, { Check, Stepper } from '../MarketplaceShell'
import HostCallout from './HostCallout'

/** Readable names for the policy's keys; anything new falls back to its key in plain words. */
const LIMIT_LABELS: Record<string, string> = {
  'api.requests_per_minute': 'Requests a minute',
  'jobs.report_concurrency': 'Reports at the same time',
  'jobs.process_concurrency': 'Processes at the same time',
  'jobs.import_concurrency': 'Imports at the same time',
}
const MACHINE_LABELS: Record<string, string> = { app: 'Application server', database: 'Database server' }
const REF_LABELS: Record<string, string> = { oci_indian_cloud: 'Indian cloud', digitalocean_class: 'DigitalOcean class', aws_mumbai_on_demand: 'AWS Mumbai' }

function plain(key: string): string {
  const s = key.split('.').pop() || key
  const t = s.replace(/_/g, ' ')
  return t.charAt(0).toUpperCase() + t.slice(1)
}
const num = (n: number) => n.toLocaleString()

function tierText(t: { base: number; per_person: number; max: number } | undefined): string {
  if (!t) return ''
  return `${num(t.base)} GB, plus ${num(t.per_person)} GB per person (up to ${num(t.max)} GB)`
}

const INCLUDED = ['Daily backups', 'Monitoring around the clock', 'Security patches and Twasta upgrades', 'Your own private database', 'Spare capacity so it stays fast', 'Support from Twasta']

const FAQ: { q: string; a: string }[] = [
  { q: 'What if we reach a limit?', a: 'At a limit only adding new records pauses — everything you already have keeps working, and you can move to a bigger plan.' },
  { q: 'Can we change between per person and a server?', a: 'Yes, from My apps.' },
  { q: 'Is hosting part of a builder’s monthly price?', a: 'Yes. When a Twasta builder makes an app for you, their monthly price includes this hosting plus their updates and fixes, so it is always above the hosting price.' },
  { q: 'How do we pay?', a: 'Each month’s price is taken from your organization wallet, which you top up by UPI or card. GST is added when you top up.' },
]

export default function HostingPricingPage() {
  const currency = 'INR'
  const [h, setH] = useState<HostingPrices | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(async () => {
    setError(null)
    try {
      setH(await getHosting(currency))
    } catch (e) {
      setError(mktError(e, "Hosting prices didn't load. Please try again.").message)
    }
  }, [])
  useEffect(() => { load() }, [load])

  return (
    <MarketplaceShell>
      <section className="mkt-hero">
        <div className="mkt-hero-in">
          <span className="mkt-kicker">Hosting prices</span>
          <h1>Host your application on Twasta</h1>
          <p>Pay for each person who uses it, or for a whole server your team shares. Backups, monitoring, updates and security are included in every price.</p>
          <HostCallout currency={currency} size="lg" />
          <span className="mkt-faint">Prices before GST. GST is added when you top up your organization wallet.</span>
        </div>
      </section>
      <div className="mkt-wrap" style={{ padding: '28px 20px 56px' }}>
        {!h && !error && <span className="mkt-muted" role="status">Loading hosting prices…</span>}
        {error && <div role="alert" className="mkt-banner bad"><div><strong>Something went wrong</strong>{error}</div><button type="button" className="btn btn-secondary" onClick={load}>Try again</button></div>}
        {h && <Prices h={h} />}
      </div>
    </MarketplaceShell>
  )
}

function Prices({ h }: { h: HostingPrices }) {
  const cur = h.currency
  const [basis, setBasis] = useState<'person' | 'server'>(h.start.basis)
  const [people, setPeople] = useState(20)
  const [open, setOpen] = useState(0)
  const sizes = h.sizes.filter((s) => s.price_minor > 0)
  const maxPeople = Math.max(1000, ...sizes.map((s) => s.max_people || 0))
  const personCost = h.per_person_minor * people
  const fit = sizes.slice().sort((a, b) => a.price_minor - b.price_minor).find((s) => (s.max_people || 0) >= people)
  const options = [
    ...(h.per_person_minor ? [{ key: 'person', t: 'Per person', v: personCost, d: `${people} × ${money(h.per_person_minor, cur)} a month` }] : []),
    ...(fit ? [{ key: 'server', t: `${fit.name} server`, v: fit.price_minor, d: `up to ${fit.max_people} people, a month` }] : []),
  ]
  const cheapest = options.length > 1 ? Math.min(...options.map((o) => o.v)) : null
  const refKeys = Array.from(new Set(sizes.flatMap((s) => Object.keys(s.market_reference || {}))))

  return (
    <div className="mkt-col" style={{ gap: 24 }}>
      <div className="mkt-row" style={{ justifyContent: 'center' }}>
        <div className="mkt-chips" role="group" aria-label="How to pay">
          {([['person', 'Per person'], ['server', 'Per server']] as const).map(([id, t]) => (
            <button key={id} type="button" className={`mkt-chip${basis === id ? ' on' : ''}`} aria-pressed={basis === id} onClick={() => setBasis(id)} style={{ minWidth: 140 }}>{t}</button>
          ))}
        </div>
      </div>

      {basis === 'person' && (
        <section className="mkt-panel mkt-row" style={{ gap: 28, alignItems: 'flex-start', padding: 28 }}>
          <div className="mkt-col" style={{ flex: '1 1 300px', gap: 8 }}>
            {h.start.basis === 'person' && <span className="mkt-pill info" style={{ alignSelf: 'flex-start' }}>Lowest starting price</span>}
            <h2 style={{ fontSize: 24 }}>Per person</h2>
            <div><strong style={{ fontSize: 36 }}>{h.per_person_minor ? money(h.per_person_minor, cur) : '—'}</strong> <span className="mkt-muted">per person a month</span></div>
            <p className="mkt-muted" style={{ margin: 0 }}>Best for small teams and teams that grow and shrink. You pay only for the people who can sign in.</p>
          </div>
          <ul className="mkt-col" style={{ flex: '1 1 300px', listStyle: 'none', margin: 0, padding: 0, gap: 10 }}>
            {h.per_person_tier.storage_gb && <Li>Data storage: {tierText(h.per_person_tier.storage_gb)}</Li>}
            {h.per_person_tier.files_gb && <Li>Files and documents: {tierText(h.per_person_tier.files_gb)}</Li>}
            <Li>Speed limits that grow with your team</Li>
            <Li>Add or remove people any time</Li>
          </ul>
        </section>
      )}

      {basis === 'server' && (
        sizes.length === 0
          ? <div className="mkt-empty"><strong>No server sizes are offered right now</strong></div>
          : <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>{sizes.map((s) => <ServerCard key={s.id} s={s} cur={cur} />)}</div>
      )}

      <section className="mkt-panel mkt-col" style={{ gap: 16 }} aria-labelledby="hp-cost">
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <h2 id="hp-cost">What would it cost us?</h2>
          <div className="mkt-row"><span className="mkt-muted">How many people will use the application?</span><Stepper value={people} onChange={setPeople} min={1} max={maxPeople} label="people" /></div>
        </div>
        <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }} aria-live="polite">
          {options.map((o) => {
            const best = cheapest != null && o.v === cheapest
            return (
              <div key={o.key} className="mkt-col" style={{ gap: 4, padding: 18, borderRadius: 14, border: `2px solid ${best ? 'var(--color-success)' : 'var(--color-border)'}`, background: best ? 'var(--color-success-weak)' : 'var(--color-bg)' }}>
                <span className="mkt-muted">{o.t}</span>
                <strong style={{ fontSize: 28 }}>{money(o.v, cur)}</strong>
                <span className="mkt-faint">{o.d}</span>
                {best && <span className="mkt-pill ok" style={{ alignSelf: 'flex-start' }}>Cheaper for {people} people</span>}
              </div>
            )
          })}
          {!fit && sizes.length > 0 && <span className="mkt-muted">No single server size is offered for {people} people — talk to Twasta about a larger setup.</span>}
        </div>
      </section>

      <div className="mkt-split">
        <section className="mkt-panel mkt-col" style={{ flex: '1 1 300px', gap: 10 }}>
          <h2>Included in every price</h2>
          <ul className="mkt-col" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 10 }}>{INCLUDED.map((i) => <Li key={i}>{i}</Li>)}</ul>
        </section>
        <section className="mkt-panel mkt-col" style={{ flex: '999 1 520px', minWidth: 0, gap: 10 }}>
          <h2>How we set these prices</h2>
          <p className="mkt-muted" style={{ margin: 0 }}>
            Server cost, plus {h.management_percent ?? 0}% for running it (backups, monitoring, upgrades, support, spare capacity), plus {h.margin_percent ?? 0}% margin.
            {refKeys.length > 0 && ' For comparison, what the same machines cost elsewhere each month:'}
          </p>
          {sizes.length > 0 && (
            <div className="mkt-scroll">
              <table className="mkt-table" style={{ minWidth: 560 }}>
                <thead><tr><th scope="col">Server</th>{refKeys.map((k) => <th key={k} scope="col">{REF_LABELS[k] || plain(k)}</th>)}<th scope="col" className="num">Twasta, managed</th></tr></thead>
                <tbody>{sizes.map((s) => <tr key={s.id}><td><strong>{s.name}</strong></td>{refKeys.map((k) => <td key={k}>{s.market_reference?.[k] || '—'}</td>)}<td className="num"><strong>{money(s.price_minor, cur)}</strong></td></tr>)}</tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <section className="mkt-panel mkt-col" style={{ gap: 0 }}>
        <h2 style={{ marginBottom: 8 }}>Questions</h2>
        {FAQ.map((f, i) => (
          <div key={f.q} style={{ borderTop: '1px solid var(--color-border)' }}>
            <button type="button" aria-expanded={open === i} aria-controls={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
              style={{ width: '100%', textAlign: 'left', font: 'inherit', fontSize: 16, fontWeight: 600, color: 'var(--color-text)', background: 'transparent', border: 0, padding: '16px 4px', display: 'flex', justifyContent: 'space-between', gap: 16, cursor: 'pointer', minHeight: 44 }}>
              <span>{f.q}</span><span aria-hidden="true">{open === i ? '−' : '+'}</span>
            </button>
            {open === i && <p id={`faq-${i}`} className="mkt-muted" style={{ margin: '0 4px 16px' }}>{f.a}</p>}
          </div>
        ))}
      </section>
    </div>
  )
}

function ServerCard({ s, cur }: { s: HostingSize; cur: string }) {
  const rows: [string, string][] = [
    ...Object.entries(s.machines || {}).map(([k, v]) => [MACHINE_LABELS[k] || plain(k), v] as [string, string]),
    ...(s.storage_gb ? [['Data storage', `${num(s.storage_gb)} GB`] as [string, string]] : []),
    ...(s.files_gb ? [['Files and documents', `${num(s.files_gb)} GB`] as [string, string]] : []),
    ...Object.entries(s.limits || {}).map(([k, v]) => [LIMIT_LABELS[k] || plain(k), num(v)] as [string, string]),
  ]
  return (
    <section className="mkt-panel mkt-col" style={{ gap: 10 }} aria-label={`${s.name} server`}>
      <h2 style={{ fontSize: 21 }}>{s.name} server</h2>
      <div><strong style={{ fontSize: 30 }}>{money(s.price_minor, cur)}</strong> <span className="mkt-muted">a month</span></div>
      {s.max_people > 0 && <span className="mkt-muted" style={{ fontSize: 14 }}>Up to <strong>{s.max_people} people</strong> · about {money(Math.round(s.price_minor / s.max_people), cur)} per person when full</span>}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 14 }}>
        {rows.map(([l, v]) => (
          <li key={l} className="mkt-row" style={{ justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--color-border)' }}><span className="mkt-muted">{l}</span><span style={{ fontWeight: 600 }}>{v}</span></li>
        ))}
      </ul>
    </section>
  )
}

function Li({ children }: { children: ReactNode }) {
  return <li className="mkt-row" style={{ flexWrap: 'nowrap', alignItems: 'flex-start', gap: 8 }}><span style={{ color: 'var(--color-success)', marginTop: 2 }}><Check size={18} /></span><span>{children}</span></li>
}
